import { useRef, useState } from 'react';

import type {
  ClearedMove, InlineToolCall, NextActionsProgress, Recommendation, RecommendationsToolCall,
} from '../tools/types';
import type { Message, PageContext } from '../types';

export interface NextActionQueue {
  cleared: ClearedMove[];
  createdAt: string;
  dismissed: boolean;
  id: number;
  recommendations: Recommendation[];
  snoozePath?: string;
  turn: number;
}

interface Browsing {
  pagedAt?: number;
  position?: number;
  toggled?: { at: string; open: boolean };
}

export function useNextActions() {
  const [browsing, setBrowsing] = useState<Record<number, Browsing>>({});

  const browsingRef = useRef(browsing);
  browsingRef.current = browsing;

  function browsingFor(queue: NextActionQueue) {
    return browsingRef.current[queue.id] || stored<Browsing>(browsingKey(queue.id));
  }

  function browse(queue: NextActionQueue, changes: Partial<Browsing>) {
    const next = { ...browsingFor(queue), ...changes };

    browsingRef.current = { ...browsingRef.current, [queue.id]: next };
    setBrowsing(all => ({ ...all, [queue.id]: next }));
    store(browsingKey(queue.id), next);
  }

  function page(queue: NextActionQueue, step: number) {
    const { index, remaining } = currentView(queue, browsingFor(queue));

    const { position } = remaining[(index + step + remaining.length) % remaining.length];

    browse(queue, { pagedAt: queue.cleared.length, position });
  }

  function toggle(queue: NextActionQueue, messages: Message[], open: boolean) {
    browse(queue, { toggled: { at: moment(queue, messages), open } });
  }

  function view(queue: NextActionQueue, messages: Message[]) {
    const { pagedAt, toggled } = browsingFor(queue);
    const open = toggled?.at === moment(queue, messages) ? toggled.open : !folded(queue, messages);
    const marked = justDone(queue, messages) && pagedAt !== queue.cleared.length;

    return { ...currentView(queue, browsingFor(queue)), marked, open };
  }

  // A folded card is still worth reporting, because sending is what folds it.
  function context(messages: Message[]): PageContext | null {
    const queue = latestRecommendations(messages);
    if (!queue || queue.dismissed) {
      return null;
    }

    const { recommendation } = currentView(queue, browsingFor(queue));
    if (!recommendation) {
      return null;
    }

    const done = queue.cleared.filter(move => move.as === 'done').map(move => move.position);

    return {
      nextActionPosition: String(recommendation.position),
      nextActionTitle: recommendation.title ?? undefined,
      nextActionUrl: recommendation.url,
      ...(done.length > 0 && { nextActionsCleared: done.join(',') }),
    };
  }

  return { browse, context, page, toggle, view };
}

export function mergePageContext(page: PageContext | null, nextAction: PageContext | null) {
  return page || nextAction ? { ...page, ...nextAction } : null;
}

export function latestRecommendations(messages: Message[]) {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const queue = recommendationQueue(messages[index].inlineToolCalls || [], userTurns(messages.slice(0, index)));

    if (queue) {
      return queue.recommendations.length > 0 && raisedToday(queue.createdAt) ? queue : null;
    }
  }

  return null;
}

export function recommendationQueue(toolCalls: InlineToolCall[], turn = 0): NextActionQueue | null {
  const call = toolCalls.filter(isRecommendations).pop();
  if (!call) {
    return null;
  }

  const { cleared = [], dismissed = false, recommendations = [], snoozePath } = call.metadata;

  return { cleared, createdAt: call.createdAt, dismissed, id: call.id, recommendations, snoozePath, turn };
}

export function remaining(queue: NextActionQueue) {
  return queue.recommendations.filter(({ position }) => !queue.cleared.some(move => move.position === position));
}

export function cleared(queue: NextActionQueue) {
  return queue.dismissed || remaining(queue).length === 0;
}

export interface DoneMove {
  key: string;
  recommendation: Recommendation;
  turn: number;
}

export function doneMoves(messages: Message[]) {
  const calls = messages.reduce<RecommendationsCall[]>(
    (all, message) => all.concat((message.inlineToolCalls || []).filter(isRecommendations)),
    []
  );

  return calls.reduce<DoneMove[]>((all, call) => {
    const { cleared = [], recommendations = [] } = call.metadata;

    cleared.filter(move => move.as === 'done').forEach((move) => {
      const recommendation = recommendations.find(({ position }) => position === move.position);
      if (recommendation) {
        all.push({ key: `${call.id}:${move.position}`, recommendation, turn: move.turn });
      }
    });

    return all;
  }, []);
}

function justDone(queue: NextActionQueue, messages: Message[]) {
  const last = queue.cleared[queue.cleared.length - 1];
  return last?.as === 'done' && last.turn === userTurns(messages);
}

export function userTurns(messages: Message[]) {
  return messages.filter(({ role }) => role === 'user').length;
}

function currentView(queue: NextActionQueue, { position }: Browsing) {
  const left = remaining(queue);
  const index = Math.max(0, left.findIndex(card => card.position === position));

  return { index, recommendation: left[index], remaining: left };
}

function folded(queue: NextActionQueue, messages: Message[]) {
  const done = queue.cleared.filter(move => move.as === 'done').map(move => move.turn);

  return userTurns(messages) > Math.max(queue.turn, ...done);
}

function moment(queue: NextActionQueue, messages: Message[]) {
  return `${userTurns(messages)}:${queue.cleared.length}`;
}

type RecommendationsCall = InlineToolCall & RecommendationsToolCall;

function isRecommendations(toolCall: InlineToolCall): toolCall is RecommendationsCall {
  return toolCall.name === 'recommendations';
}

export function storeProgress(toolCallId: number, progress: NextActionsProgress) {
  store(progressKey(toolCallId), progress);
}

export function withStoredProgress(messages: Message[]): Message[] {
  return messages.map(message => (
    message.inlineToolCalls?.some(isRecommendations)
      ? {
        ...message,
        inlineToolCalls: message.inlineToolCalls.map(toolCall => (
          isRecommendations(toolCall)
            ? {
              ...toolCall,
              metadata: { ...toolCall.metadata, ...stored<NextActionsProgress>(progressKey(toolCall.id)) },
            }
            : toolCall
        )),
      }
      : message
  ));
}

function browsingKey(toolCallId: number) {
  return `ai-assistant-next-actions-${toolCallId}`;
}

function progressKey(toolCallId: number) {
  return `ai-assistant-next-actions-progress-${toolCallId}`;
}

function stored<T>(key: string): Partial<T> {
  try {
    return JSON.parse(window.sessionStorage.getItem(key) || '{}') as Partial<T>;
  } catch {
    return {};
  }
}

function store(key: string, value: object) {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // The card forgets where it was when the page reloads.
  }
}

function raisedToday(createdAt: string) {
  return new Date(createdAt).toDateString() === new Date().toDateString();
}
